import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, ArrowRight, Loader2, ChevronDown, ChevronUp, X } from 'lucide-react';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerClose,
} from '@/components/ui/drawer';
import InlineStockModal from '@/components/InlineStockModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// How many chips to render before collapsing the rest behind a
// "Show all" expander. 6 keeps the card visually compact at the
// bottom of a long guide while still demonstrating breadth.
const CHIPS_COLLAPSED = 6;

/**
 * LearnTrapBuyExampleCard
 * -----------------------
 * Card on the Traps + Triangle Learn guides showing a King/
 * Aristocrat (Achievers fallback) whose DiviDrip opinion verdict
 * is currently BUY. Cascade: K + A first, only dip into Achievers
 * when neither tier yields a buy.
 *
 * Now also surfaces the OTHER buy-rated tickers from the same scan
 * as small chip-links underneath the headline ticker — so readers
 * see the engine works at scale, not just on the alphabetically-
 * first match. Headline ticker is NEVER duplicated in the chips.
 *
 * Render states:
 *   • loading → spinner card with the same emerald border so the
 *     layout doesn't pop in
 *   • no buys found anywhere → component returns null (quiet hide)
 *   • normal → headline ticker card + "Open Triangle tab" link +
 *     "Also rated BUY today" chip row when others.length > 0
 *
 * Props:
 *   context — "trap" | "triangle" — tunes the intro copy
 */
export default function LearnTrapBuyExampleCard({ context = 'trap' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllChips, setShowAllChips] = useState(false);
  // Tap-preview drawer: holds the `other` object the user tapped.
  // Same component on mobile + desktop — gives everyone a preview
  // of the company name + buy headline before they commit to
  // opening the full Stock Modal.
  const [preview, setPreview] = useState(null);

  // Inline Stock Modal — opens RIGHT HERE on the Learn page instead
  // of navigating to /?stock=X (which would unmount the guide and
  // render the Dashboard underneath). When the user closes the
  // modal the Learn page is still mounted so they're immediately
  // back to where they were reading. `InlineStockModal` is the
  // codebase's purpose-built component for this case — its header
  // comment literally calls out the "ticker links jumping the user
  // back to the Dashboard" bug we just hit.
  const [openTicker, setOpenTicker] = useState(null);
  const openStockModal = (ticker) => setOpenTicker(ticker);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/learn/buy-example-ticker`);
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Loading state — shows the card shell with a spinner where the
  // link will be. The endpoint can take ~30-90s on a cold day when
  // the K/A triangle cache is mostly empty (e.g., after a deploy
  // or weekly cache flush), so we always show progress instead of
  // a layout pop. The card sits at the bottom of long guides, so
  // by the time most readers scroll to it the spinner has already
  // resolved.
  if (loading) {
    return (
      <div
        className="rounded-lg border-2 border-emerald-400/40 bg-emerald-50/30 dark:bg-emerald-950/15 px-4 py-3 my-4"
        data-testid="learn-trap-buy-example-card-loading"
      >
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-emerald-600/70 dark:text-emerald-400/70 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-emerald-800/80 dark:text-emerald-200/80 m-0">
              Finding a healthy example…
            </h3>
            <p className="text-xs text-muted-foreground mt-1 mb-2">
              Checking every Dividend King and Aristocrat for a current
              BUY verdict against the live opinion engine.
            </p>
            <div className="inline-flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300 font-semibold">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Scanning…
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data?.ticker) return null;

  const tierLabel = data.tier === 'king'
    ? 'Dividend King'
    : data.tier === 'aristocrat'
      ? 'Dividend Aristocrat'
      : data.tier === 'achiever'
        ? 'Dividend Achiever'
        : 'Quality dividend payer';

  const intro = context === 'triangle' ? (
    <>
      Want to see the score in action? Here&apos;s a{' '}
      <strong>{tierLabel}</strong> the DiviDrip opinion engine is currently
      rating <strong>BUY</strong>:
    </>
  ) : (
    <>
      To get a feel for the contrast — here&apos;s a{' '}
      <strong>{tierLabel}</strong> the DiviDrip opinion engine is currently
      calling <strong>BUY</strong>:
    </>
  );
  const headline = context === 'triangle'
    ? 'See a healthy score in action'
    : 'See a healthy example';
  const footnote = context === 'triangle' ? (
    <>
      Notice how all three Triangle legs hold up, the payout-ratio modifier
      stays green, and the trap detector quietly stays silent — exactly what
      a clean Triangle is supposed to look like. Educational signal only, not
      financial advice.
    </>
  ) : (
    <>
      Scroll the trap warning banner, the &quot;Why this score?&quot;
      bullets, and the radar — then sort your watchlist by yield descending
      to see how often the trap banner fires on the cheap-looking names
      instead. Educational signal only, not financial advice.
    </>
  );

  const others = Array.isArray(data.others) ? data.others : [];

  return (
    <div
      className="rounded-lg border-2 border-emerald-400/60 bg-emerald-50/40 dark:bg-emerald-950/20 px-4 py-3 my-4"
      data-testid="learn-trap-buy-example-card"
      data-context={context}
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-emerald-800 dark:text-emerald-200 m-0">
            {headline}
          </h3>
          <p className="text-xs text-foreground/85 mt-1 mb-2">
            {intro}
          </p>
          <div className="rounded-md border bg-card px-3 py-2 mb-2">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono font-bold text-lg" data-testid="learn-trap-buy-ticker">{data.ticker}</span>
              <span className="text-xs text-muted-foreground">{data.name}</span>
            </div>
            {data.headline && (
              <p className="text-xs text-foreground/85 mt-1 italic">
                &ldquo;{data.headline}&rdquo;
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => openStockModal(data.ticker)}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 dark:text-emerald-300 hover:underline"
            data-testid="learn-trap-buy-example-link"
          >
            Open {data.ticker}&apos;s Triangle tab
            <ArrowRight className="w-3.5 h-3.5" />
          </button>

          {/* Chip row of the OTHER current BUY-rated K/A names from
              the same scan. Headline ticker is excluded server-side
              so we never show duplicates. Collapsed to 6 chips by
              default; "Show all (N)" expander pops the rest for the
              curious reader without bloating the card by default. */}
          {others.length > 0 && (() => {
            const overflowCount = Math.max(0, others.length - CHIPS_COLLAPSED);
            const visible = showAllChips ? others : others.slice(0, CHIPS_COLLAPSED);
            return (
              <div className="mt-3" data-testid="learn-trap-buy-others">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
                  Also rated BUY today
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {visible.map((o) => (
                    <button
                      key={o.ticker}
                      type="button"
                      onClick={() => setPreview(o)}
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-400/60 bg-background/70 px-2 py-1 text-[11px] font-mono font-bold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors"
                      title={`${o.name} — tap for buy headline`}
                      data-testid={`learn-trap-buy-other-${o.ticker}`}
                    >
                      {o.ticker}
                      <span className="text-[10px] opacity-70 font-sans font-normal">
                        {o.tier === 'king' ? 'King'
                          : o.tier === 'aristocrat' ? 'Aris'
                            : o.tier === 'achiever' ? 'Ach'
                              : ''}
                      </span>
                    </button>
                  ))}
                  {overflowCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAllChips((v) => !v)}
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-400/40 bg-emerald-50/40 dark:bg-emerald-950/30 px-2 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100/60 dark:hover:bg-emerald-950/60 transition-colors"
                      data-testid="learn-trap-buy-others-toggle"
                    >
                      {showAllChips ? (
                        <>
                          <ChevronUp className="w-3 h-3" />
                          Show fewer
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3" />
                          Show all ({others.length})
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          <p className="text-[10px] text-muted-foreground mt-2 italic">
            {footnote}
          </p>
        </div>
      </div>

      {/* Chip tap preview — same drawer on mobile + desktop. Stays
          out of the way until a chip is tapped, then renders the
          company name and the buy-verdict headline so the reader can
          decide whether to open the full Stock Modal Triangle tab. */}
      <Drawer
        open={!!preview}
        onOpenChange={(o) => { if (!o) setPreview(null); }}
      >
        <DrawerContent data-testid="learn-trap-buy-other-preview">
          <div className="mx-auto w-full max-w-md">
            <DrawerHeader className="pr-12">
              <DrawerTitle className="flex items-baseline gap-2 flex-wrap">
                <span className="font-mono text-2xl font-bold">{preview?.ticker}</span>
                {preview?.tier && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded border border-emerald-400/60 bg-emerald-50/60 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 font-semibold uppercase tracking-wider">
                    {preview.tier === 'king' ? 'Dividend King'
                      : preview.tier === 'aristocrat' ? 'Dividend Aristocrat'
                        : preview.tier === 'achiever' ? 'Dividend Achiever'
                          : preview.tier}
                  </span>
                )}
              </DrawerTitle>
              <DrawerDescription className="text-sm">
                {preview?.name}
              </DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-2 space-y-3">
              {preview?.headline && (
                <div className="rounded-md border border-emerald-400/50 bg-emerald-50/40 dark:bg-emerald-950/20 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-300 mb-1">
                    Buy verdict
                  </div>
                  <p className="text-sm italic text-foreground/90 m-0">
                    &ldquo;{preview.headline}&rdquo;
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  const t = preview?.ticker;
                  setPreview(null);
                  if (t) openStockModal(t);
                }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2.5 transition-colors"
                data-testid="learn-trap-buy-other-preview-cta"
              >
                Open {preview?.ticker}&apos;s Triangle tab
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 pb-6 pt-1">
              <DrawerClose asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="learn-trap-buy-other-preview-close"
                >
                  <X className="w-3.5 h-3.5" />
                  Close
                </button>
              </DrawerClose>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Inline Stock Modal — mounts ON the Learn page (not via a
          /?stock= navigation), so the guide stays underneath and
          closing the modal returns the user to exactly where they
          were reading. `initialTab="triangle"` lands them straight
          on the Triangle tab that matters for the buy headline. */}
      <InlineStockModal
        ticker={openTicker}
        onClose={() => setOpenTicker(null)}
        initialTab="triangle"
      />
    </div>
  );
}
